import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Waves, ArrowRight } from "lucide-react";
import { SPECTACLES_RESERVABLES } from "../lib/spectacleReservations";

const API = process.env.REACT_APP_BACKEND_URL;

const echeance = (s) => new Date(`${s.date_iso}T${s.heure_iso || "19:00"}:00+01:00`).getTime();

function restant(cible) {
  const ms = cible - Date.now();
  if (ms <= 0) return null;
  return {
    jours: Math.floor(ms / 86400000),
    heures: Math.floor((ms % 86400000) / 3600000),
    minutes: Math.floor((ms % 3600000) / 60000),
  };
}

/**
 * Bandeau d'accueil : compte à rebours du prochain spectacle + places restantes en direct.
 * Disparaît tout seul une fois le spectacle passé — personne n'a à venir le retirer.
 */
export const SpectacleCountdown = () => {
  const spectacle = SPECTACLES_RESERVABLES.filter((s) => s.date_iso).sort(
    (a, b) => echeance(a) - echeance(b)
  )[0];
  const [temps, setTemps] = useState(() => (spectacle ? restant(echeance(spectacle)) : null));
  const [places, setPlaces] = useState(null);

  useEffect(() => {
    if (!spectacle) return;
    const tick = () => setTemps(restant(echeance(spectacle)));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [spectacle]);

  useEffect(() => {
    if (!spectacle) return;
    let annule = false;
    fetch(`${API}/api/spectacles/${spectacle.id}/places`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!annule && d) setPlaces(d);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [spectacle]);

  if (!spectacle || !temps) return null;

  const cases = [
    { v: temps.jours, l: temps.jours > 1 ? "jours" : "jour" },
    { v: temps.heures, l: temps.heures > 1 ? "heures" : "heure" },
    { v: temps.minutes, l: "min" },
  ];

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 md:px-12 py-10"
      data-testid="home-spectacle-countdown"
      aria-labelledby="spectacle-countdown-title"
    >
      <div
        className="rounded-3xl p-7 md:p-10 relative overflow-hidden"
        style={{ background: "#0F1B2D", color: "#F8FAFC" }}
      >
        <div
          className="absolute -top-24 -right-16 w-[380px] h-[380px] rounded-full pointer-events-none"
          style={{ background: "var(--primary)", opacity: 0.18, filter: "blur(20px)" }}
        />
        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="eyebrow mb-3" style={{ color: "var(--primary)" }}>
              Prochain spectacle · entrée libre
            </div>
            <h2 id="spectacle-countdown-title" className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
              {spectacle.title}
            </h2>
            <p className="text-base mt-3" style={{ color: "#CBD5E1" }}>
              {spectacle.date_label} · {spectacle.time_label} · {spectacle.location}
              <br />
              {spectacle.doors_label} · {spectacle.restauration}
            </p>

            <div className="flex items-start gap-2 mt-5 text-sm" style={{ color: "#CBD5E1" }}>
              <Waves size={18} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
              <span>
                Don libre sur place — bénéfices intégralement reversés à{" "}
                <a
                  href={spectacle.association.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold underline"
                  style={{ color: "#F8FAFC" }}
                >
                  {spectacle.association.name}
                </a>
                , pour la préservation des océans.
              </span>
            </div>
          </div>

          <div>
            <div className="flex gap-3" data-testid="spectacle-countdown-timer">
              {cases.map((c) => (
                <div
                  key={c.l}
                  className="flex-1 rounded-2xl px-3 py-4 text-center"
                  style={{ background: "rgba(248,250,252,0.07)", border: "1px solid rgba(248,250,252,0.12)" }}
                >
                  <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "var(--primary)" }}>
                    {c.v}
                  </div>
                  <div className="text-xs mt-2" style={{ color: "#94A3B8" }}>
                    {c.l}
                  </div>
                </div>
              ))}
            </div>

            {places && (
              <div className="mt-5" data-testid="spectacle-countdown-places">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">
                    {places.full
                      ? "Complet — liste d'attente ouverte"
                      : `${places.remaining} place${places.remaining > 1 ? "s" : ""} restante${places.remaining > 1 ? "s" : ""}`}
                  </span>
                  <span className="text-xs" style={{ color: "#94A3B8" }}>
                    sur {places.capacity}
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "rgba(248,250,252,0.12)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, Math.round((places.reserved / places.capacity) * 100))}%`,
                      background: places.full ? "#DC2626" : "var(--primary)",
                    }}
                  />
                </div>
              </div>
            )}

            <Link
              to={`/inscription/spectacle?spectacle=${spectacle.id}`}
              data-testid="spectacle-countdown-cta"
              className="btn-primary w-full justify-center mt-5"
            >
              <Ticket size={16} />
              {places && places.full ? "Rejoindre la liste d'attente" : "Réserver une place"}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
